import classNames from 'classnames';
import Immutable from 'immutable';
import React from 'react';

import Spinner from 'sharedreact/components/Spinner';
import ValidationInput from 'sharedreact/components/ValidationInput';
import { translate } from 'sharedreact/utils/LanguageSupport';

const dropDownActionCount = 1; // The 'More...' item added to the bottom of the dropdown

type Props = {
  id: string,
  className?: string,
  listIcon?: string,
  name: string,
  value?: string,
  placeholder?: string,
  suggestions?: Immutable.List<string>,
  totalSuggestionCount?: number,
  waiting?: boolean,
  requestFocus?: boolean,
  disabled?: boolean,
  missingResource?: boolean,
  missingResourceName?: string,
  tooltip?: string,
  validations?: string[],
  noSuggestions?: boolean,

  validate?: (name: string) => void,
  onApplyNonMatch?: (value: string, event?: React.FocusEvent<HTMLInputElement>) => void,
  onChangeValue: (name: string, filterString: string, event: React.ChangeEvent<HTMLInputElement>) => void,
  onGetSuggestions: () => void,
  onSuggestionSelected: (suggestionIndex: number, event?: React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLUListElement> | React.FocusEvent<HTMLInputElement>) => void,
  onShowResource?: ((filterString: string | null, name: string, event?: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLUListElement>) => void) | null,
  onClearResource: (event?: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLButtonElement>) => void,
  onRestoreResource: (event?: React.FocusEvent<HTMLInputElement>) => void,
  getInputAddon?: () => React.ReactNode,
  onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void,
  isEmpty?: (filterString: string) => boolean
};

type State = {
  hasFocus: boolean,
  showDropDown: boolean,
  waiting: boolean,
  cachedWaitingProps: boolean,
  keyboardSelection: number,
};

function _isEmpty (filterString: string): boolean {
  return filterString.length === 0;
}

type DefaultProps = Required<Pick<Props, 'listIcon' | 'value' | 'placeholder' | 'suggestions' | 'totalSuggestionCount' | 'waiting' | 'missingResource' | 'missingResourceName' | 'validations' | 'noSuggestions' | 'isEmpty'>>;

type PropsWithDefaults = Omit<Props, keyof DefaultProps> & DefaultProps;

class ResourceInput extends React.PureComponent<Props, State> {
   static defaultProps: DefaultProps = {
    listIcon: 'esko_icon esko_icon-more',
    value: '',
    placeholder: '',
    suggestions: Immutable.List(),
    totalSuggestionCount: -1,
    waiting: false,
    validations: [],
    missingResource: false,
    missingResourceName: 'shared.missing.resource.name.general',
    noSuggestions: false,
    isEmpty: _isEmpty
  };

  private textChangeTimeoutId: NodeJS.Timeout | null;
  private textClearTimeoutId: NodeJS.Timeout | null;
  private dontApplyOnBlur: boolean;
  private textInputElement: HTMLInputElement | null;

  private errorAnchorElement: HTMLElement | null;

  constructor (props: Props) {
    super(props);

    this.textChangeTimeoutId = null;
    this.textClearTimeoutId = null;
    this.dontApplyOnBlur = false;
    this.textInputElement = null;
    this.errorAnchorElement = null;

    const waiting = props.waiting ?? false;

    this.state = {
      hasFocus: false,
      showDropDown: false,
      waiting: waiting,
      cachedWaitingProps: waiting,
      keyboardSelection: -1
    };

    this.updateSuggestions = this.updateSuggestions.bind(this);
    this.handleTextInputChange = this.handleTextInputChange.bind(this);
    this.handleTextInputKeyDown = this.handleTextInputKeyDown.bind(this);
    this.handleTextInputFocus = this.handleTextInputFocus.bind(this);
    this.handleTextInputBlur = this.handleTextInputBlur.bind(this);
    this.handleSuggestionClick = this.handleSuggestionClick.bind(this);
    this.handleSuggestionSelected = this.handleSuggestionSelected.bind(this);
    this.handleClearButtonClick = this.handleClearButtonClick.bind(this);
    this.handleButtonMouseDown = this.handleButtonMouseDown.bind(this);
    this.handleListButtonClick = this.handleListButtonClick.bind(this);
    this.handleDropDownLinkMouseDown = this.handleDropDownLinkMouseDown.bind(this);
    this.handleMoreSelected = this.handleMoreSelected.bind(this);
  }

  updateSuggestions (): void {
    this.props.onGetSuggestions();

    this.textChangeTimeoutId = null;

    if (this.props.validate) {
      this.props.validate(this.props.name);
    }
  }

  debouncedClearText (): void {
    this.textClearTimeoutId = null;

    if (this.props.validate) {
      this.props.validate(this.props.name);
    }
  }

  handleTextInputChange (event: React.ChangeEvent<HTMLInputElement>): void {
    const { onChangeValue, name } = (this.props as PropsWithDefaults);

    this.dontApplyOnBlur = false;

    const text: string = event.target.value;

    onChangeValue(name, text, event);

    if (text.length > 0) {
      this.setState({
        showDropDown: true,
        waiting: true,
        keyboardSelection: -1
      });
    } else {
      this.setState({
        showDropDown: false,
        waiting: false,
        keyboardSelection: -1
      });
    }

    if (this.textChangeTimeoutId != null) {
      clearTimeout(this.textChangeTimeoutId);
    }

    this.textChangeTimeoutId = setTimeout(() => { this.updateSuggestions(); }, 500);
  }

  handleTextInputKeyDown (event: React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLUListElement>): void {
    const { value, suggestions } = (this.props as PropsWithDefaults);
    if (event.key === 'Enter') {
      event.preventDefault();
      if (this.state.showDropDown && this.state.keyboardSelection !== -1) {
        if (this.state.keyboardSelection < suggestions.size) {
          this.handleSuggestionSelected(this.state.keyboardSelection, event);
        } else {
          this.dontApplyOnBlur = true; // early out in handleTextInputBlur
          this.handleMoreSelected(value, event);
        }
      } else {
        this.textInputElement!.blur();
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (this.state.showDropDown) {
        this.setState((prevState: State, props: Props) => {
          const { suggestions, totalSuggestionCount, waiting } = (props as PropsWithDefaults);

          let ddac = dropDownActionCount;
          if (totalSuggestionCount !== -1 && !waiting && totalSuggestionCount <= suggestions.size) {
            ddac -= 1;
          }

          if (prevState.keyboardSelection + 1 < (suggestions.size + ddac)) {
            return {keyboardSelection: prevState.keyboardSelection + 1};
          } else {
            return {keyboardSelection: 0};
          }
        });
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (this.state.showDropDown) {
        this.setState((prevState: State, props: Props) => {
          const { suggestions, totalSuggestionCount, waiting } = (props as PropsWithDefaults);
          let ddac = dropDownActionCount;
          if (totalSuggestionCount !== -1 && !waiting && totalSuggestionCount <= suggestions.size) {
            ddac -= 1;
          }

          if (prevState.keyboardSelection > 0) {
            return {keyboardSelection: prevState.keyboardSelection - 1};
          } else {
            return {keyboardSelection: suggestions.size + ddac - 1};
          }
        });
      }
    } else if (event.key === 'Escape') {
      if (this.state.showDropDown) {
        this.setState({
          showDropDown: false,
          keyboardSelection: -1
        });
      }
    }
  }

  handleTextInputFocus (event: React.FocusEvent<HTMLInputElement>): void {
    this.setState({ hasFocus: true });
  }

  handleTextInputBlur (event: React.FocusEvent<HTMLInputElement>): void {
    const dontApplyOnBlur = this.dontApplyOnBlur;

    this.dontApplyOnBlur = false;

    this.setState({ hasFocus: false });

    if (dontApplyOnBlur) {
      return;
    }

    const {
      suggestions, value, onApplyNonMatch, onClearResource, onRestoreResource
    } = (this.props as PropsWithDefaults);

    if (value.length === 0) {
      onClearResource(event);

      this.setState({
        showDropDown: false,
        keyboardSelection: -1
      });
    } else {
      const suggestionIndex = suggestions.findIndex((suggestion) => {
        return suggestion.toUpperCase() === value.toUpperCase();
      });

      if (suggestionIndex >= 0) {
        this.handleSuggestionSelected(suggestionIndex, event);
      } else {
        if (onApplyNonMatch != null) {
          onApplyNonMatch(value, event);
        } else {
          onRestoreResource(event);
        }

        this.setState({
          showDropDown: false,
          keyboardSelection: -1
        });
      }
    }

    if (this.props.onBlur != null) {
      this.props.onBlur(event);
    }
  }

  handleSuggestionClick (suggestionIndex: number, event: React.MouseEvent<HTMLAnchorElement>): void {
    this.dontApplyOnBlur = false;
    this.handleSuggestionSelected(suggestionIndex, event);
    this.textInputElement!.focus();
  }

  handleSuggestionSelected (suggestionIndex: number, event: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLAnchorElement> | React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLUListElement>): void {
    const { onSuggestionSelected } = (this.props as PropsWithDefaults);

    onSuggestionSelected(suggestionIndex, event);

    this.setState({
      showDropDown: false,
      keyboardSelection: -1
    });
  }

  handleMoreSelected (text: string | null, event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLInputElement> | React.KeyboardEvent<HTMLUListElement>): void {
    const { onShowResource, name } = (this.props as PropsWithDefaults);

    this.setState({
      showDropDown: false,
      keyboardSelection: -1,
      hasFocus: false
    });

    setTimeout(() => {
      if (onShowResource != null) {
        onShowResource(text, name, event);
      }
    }, 0);
  }

  handleClearButtonClick (event: React.MouseEvent<HTMLButtonElement>): void {
    const { onClearResource } = (this.props as PropsWithDefaults);

    onClearResource(event);

    this.setState({
      showDropDown: false,
      keyboardSelection: -1
    });

    if (this.textClearTimeoutId != null) {
      clearTimeout(this.textClearTimeoutId);
    }
    this.textClearTimeoutId = setTimeout(() => { this.debouncedClearText(); }, 500);

    this.textInputElement!.focus();
  }

  handleButtonMouseDown (event: React.MouseEvent<HTMLButtonElement>): void {
    this.dontApplyOnBlur = true; // early out in handleTextInputBlur
  }

  handleListButtonClick (event: React.MouseEvent<HTMLButtonElement>): void {
    this.handleMoreSelected(null, event);
  }

  handleDropDownLinkMouseDown (event: React.MouseEvent<HTMLAnchorElement>): void {
    this.dontApplyOnBlur = true;
  }

  static getDerivedStateFromProps (nextProps: Props, prevState: State) {
    if (nextProps.waiting !== prevState.cachedWaitingProps) {
      if (nextProps.waiting !== prevState.waiting) {
        return { cachedWaitingProps: nextProps.waiting, waiting: nextProps.waiting };
      } else {
        return { cachedWaitingProps: nextProps.waiting };
      }
    }
    return null;
  }

  override componentDidMount (): void {
    const { requestFocus } = (this.props as PropsWithDefaults);

    if (requestFocus) {
      this.textInputElement!.focus();
    }
  }

  override componentDidUpdate (): void {
    const { requestFocus } = (this.props as PropsWithDefaults);

    if (requestFocus) {
      this.textInputElement!.focus();
    }
  }

  renderSuggestion (suggestion: string, suggestionIndex: number, text: string) {
    if (text == null || text.length === 0) {
      return suggestion;
    }

    const quote = (s: string) => s.replace(/[.?*+^$[\]\\(){}|-]/g, '\\$&');
    const regexp: RegExp = new RegExp('(' + quote(text) + ')', 'i');
    const suggestionParts: string[] = suggestion.split(regexp);

    let retval = suggestionParts.map((suggestionPart, index) => {
      if (index === 1) {
        return <b key={'suggestion-' + suggestionIndex + '-match'}>{suggestionPart}</b>;
      } else {
        return suggestionPart;
      }
    });

    if (retval.length > 3) {
      const partsToJoin = retval.slice(2);
      retval = [retval[0], retval[1], partsToJoin.join('')];
    }

    return retval;
  }

  override render (): React.ReactNode {
    const {
      id, className, listIcon, name, value, placeholder,
      suggestions, totalSuggestionCount, disabled, tooltip, isEmpty,
      validations, missingResource, missingResourceName, getInputAddon,
      noSuggestions, onShowResource
    } = (this.props as PropsWithDefaults);

    const { showDropDown, waiting = false, keyboardSelection } = this.state;
    const showMissingResourceWarning = missingResource && value !== '';
    const inputAddon = getInputAddon != null ? getInputAddon() : null;

    let dropDownItems;
    if (waiting) {
      dropDownItems = [];
      dropDownItems.push(
        <li key='waiting'><Spinner small /></li>
      );
    } else {
      if (suggestions.isEmpty() && value.length > 0) {
        dropDownItems = [];
        dropDownItems.push(
          <li key='no-matches'><p className='no-matches'>{translate('shared.general.search.nomatches', value)}</p></li>
        );
      } else {
        dropDownItems = suggestions.map((suggestion, index) => {
          return (
            <li key={suggestion + '-' + index} className={classNames({active: keyboardSelection === index})}>
              <a role='button'
                onMouseDown={this.handleDropDownLinkMouseDown}
                onClick={(event) => { this.handleSuggestionClick(index, event); }}
              >
                {this.renderSuggestion(suggestion, index, value)}
              </a>
            </li>
          );
        }).toArray();
      }
    }
    if (onShowResource != null && (totalSuggestionCount === -1 || waiting || totalSuggestionCount > suggestions.size)) {
      dropDownItems.push(
        <li key='more-divider' className='esko_divider' />
      );
      const moreText = totalSuggestionCount === -1 || waiting
        ? translate('shared.general.more')
        : translate('shared.general.xmore', totalSuggestionCount - suggestions.size);
      dropDownItems.push(
        <li key='more' className={classNames({active: keyboardSelection === suggestions.size})}>
          <a role='button'
            onMouseDown={this.handleDropDownLinkMouseDown}
            onClick={(event) => this.handleMoreSelected(value, event)}
          >
            {moreText}
          </a>
        </li>
      );
    }

    return (
      <div className={classNames('esko_dropdown', 'esko_resourceinput', {'esko_open': showDropDown}, {disabled: disabled}, className)} ref={(ref) => this.errorAnchorElement = ref}>
        <div className='esko_input-group' title={tooltip ?? undefined}>
          { inputAddon &&
            <div className='esko_input-group-addon'>
              {inputAddon}
            </div>
          }
          <div className={classNames('esko_input-group', { focus: this.state.hasFocus })} style={{width: '100%'}}>
            { showMissingResourceWarning &&
              <div
                className='esko_missingresource'
                title={translate('shared.missing.resource.message', translate(missingResourceName))}
              >
                <span className='bi bi-exclamation-triangle-fill' />
              </div>
            }
            <ValidationInput
              containerClassName={classNames({'esko_missingresource-input': showMissingResourceWarning}, className)}
              type='text'
              id={id}
              value={value}
              placeholder={placeholder}
              disabled={disabled}
              name={name}
              validations={validations}
              validationTooltipPlacement='auto right'
              validationTooltipClassName='esko_resourceinputtooltip'
              autoComplete='off'
              onChange={this.handleTextInputChange}
              onKeyDown={this.handleTextInputKeyDown}
              onFocus={this.handleTextInputFocus}
              onBlur={this.handleTextInputBlur}
              errorAnchorElement={this.errorAnchorElement}
              inputRef={(inputElement) => { this.textInputElement = inputElement; }}
            />
            <div className='esko_input-group-btn'>
              <button className={classNames('esko_btn', 'esko_btn-default', {hidden: isEmpty(value)})}
                type='button'
                disabled={disabled}
                onMouseDown={this.handleButtonMouseDown}
                onClick={this.handleClearButtonClick}
              >
                <span className='esko_icon esko_icon-remove' />
              </button>
            </div>
          </div>
          <div className='esko_input-group-btn'>
            <button className={classNames('esko_btn', 'esko_btn-default')}
              type='button'
              disabled={disabled || onShowResource == null}
              onClick={this.handleListButtonClick}
            >
              <span className={listIcon} />
            </button>
          </div>
        </div>
        {noSuggestions
          ? null
          : <ul className='esko_dropdown-menu' onKeyDown={this.handleTextInputKeyDown}>
            {dropDownItems}
          </ul>
        }
      </div>
    );
  }
}

export default ResourceInput;
